import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  NZ_DATE_CONFIG,
  NzDateConfig,
  provideNzI18n,
  ru_RU,
} from 'ng-zorro-antd/i18n';
import { registerLocaleData } from '@angular/common';
import ru from '@angular/common/locales/ru';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { environment } from 'src/environments/environment';
import { RouteInitService } from './services/route-init.service';
import { firstValueFrom } from 'rxjs';
import { AuthErrorInterceptor } from './services/interceptors/auth-error.interceptor';
import Keycloak, { KeycloakConfig, KeycloakInitOptions } from 'keycloak-js';
import { provideRouter } from '@angular/router';
import { provideKeycloak } from 'keycloak-angular';
import { NzMessageService } from 'ng-zorro-antd/message';
import { provideNzIcons } from './config/nz-icons.provider';
import { routes } from './app-routing.module';
import { AuthInterceptor } from './services/interceptors/auth.interceptor';

registerLocaleData(ru);
const NZ_DATE_CONFIG_VALUE: NzDateConfig = {
  firstDayOfWeek: 1,
};

const KEYCLOAK_CONFIG: KeycloakConfig = {
  realm: environment.realm,
  url: environment.auth_url,
  clientId: environment.clientId,
};

const KEYCLOAK_INIT_OPTIONS: KeycloakInitOptions = {
  onLoad: 'check-sso',
  responseMode: 'query',
  useNonce: true,
  silentCheckSsoRedirectUri:
    window.location.origin + '/assets/silent-check-sso.html',
};

const ANONYMOUS_ROUTES = ['/', '/login'];

export function initializeApp(): Promise<void> {
  const keycloak = inject(Keycloak);
  const routeInit = inject(RouteInitService);

  return keycloak
    .init(KEYCLOAK_INIT_OPTIONS)
    .then((loggedIn) => {
      if (loggedIn) {
        return firstValueFrom(routeInit.init());
      }

      if (!ANONYMOUS_ROUTES.includes(window.location.pathname)) {
        return keycloak.login({
          redirectUri: window.location.href,
        });
      }

      return Promise.resolve();
    })
    .catch((e) => {
      console.error('App init error', e);
    });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideNzI18n(ru_RU),
    {
      provide: NZ_DATE_CONFIG,
      useValue: NZ_DATE_CONFIG_VALUE,
    },
    provideKeycloak({
      config: KEYCLOAK_CONFIG,
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    provideAppInitializer(initializeApp),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthErrorInterceptor,
      multi: true,
    },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideNzIcons(),
    NzMessageService,
  ],
};
